import { GET_KELDAN_API_URL } from "../../../services/config";
import { IKeldanApiResponse } from "../../../types/KeldanTypes";
import {
  AddCompanyWatchRequest,
  AdilarRole,
  CompanyWatchItem,
  CompanyEventsData,
} from "../../../types/CompanyWatchTypes";
import { PepWatchListData } from "../../../types/PepTypes";
import { IPepWatchResponse } from "../PepWatch";
import { stripSSN } from "../../../services/utils";

export interface ICompanyNameResponse {
  result: number;
  descr: string | null;
  name: string;
}

export const fetchCompanyNameByKennitala = async (
  kennitala: string
): Promise<string | null> => {
  try {
    const cleaned = stripSSN(kennitala) ?? kennitala.replace(/\D/g, "");
    if (cleaned.length !== 10) return null;
    const url = `${GET_KELDAN_API_URL()}/Pep/GetCompanyNameInfo?kennitala=${cleaned}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) return null;
    const data: IKeldanApiResponse<ICompanyNameResponse> = await response.json();
    if (data.success && data.result?.result === 1) {
      return data.result.name || null;
    }
    return null;
  } catch (error) {
    console.error("Error fetching company name:", error);
    return null;
  }
};

export const addCompaniesToWatchList = async (
  companies: AddCompanyWatchRequest[]
): Promise<IKeldanApiResponse<IPepWatchResponse>> => {
  const url = `${GET_KELDAN_API_URL()}/Pep/AddToCompanyWatchList`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json; charset=utf-8" },
    body: JSON.stringify(companies),
  });
  return response.json();
};

export const removeCompaniesFromWatchList = async (
  companyIds: string[]
): Promise<IKeldanApiResponse> => {
  const params = companyIds.map((id) => `companyIds=${encodeURIComponent(id)}`).join("&");
  const url = `${GET_KELDAN_API_URL()}/Pep/RemoveFromCompanyWatchList?${params}`;
  const response = await fetch(url, { method: "DELETE" });
  return response.json();
};

export const updateCompanyWatch = async (
  companyId: string,
  pepFrequency: string,
  sanctionsFrequency: string,
  adilarIVoktun: AdilarRole[]
): Promise<IKeldanApiResponse> => {
  const params = new URLSearchParams({
    companyId,
    pepFrequency,
    sanctionsFrequency,
  });
  adilarIVoktun.forEach((role) => params.append("adilarIVoktun", role));
  const url = `${GET_KELDAN_API_URL()}/Pep/UpdateCompanyWatch?${params}`;
  const response = await fetch(url, { method: "PATCH" });
  return response.json();
};

export const fetchCompanyWatchItem = async (
  companyId: string
): Promise<CompanyWatchItem | null> => {
  try {
    const url = `${GET_KELDAN_API_URL()}/Pep/GetCompanyWatchList?searchTerm=${encodeURIComponent(
      companyId
    )}&includeCount=true&start=0&limit=50`;
    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) return null;
    const data: IKeldanApiResponse<IPepWatchResponse<CompanyWatchItem[]>> = await response.json();
    if (!data.success || !data.result?.data) return null;
    return (
      data.result.data.find((item) => item.id.companyId === companyId) ??
      data.result.data[0] ??
      null
    );
  } catch (error) {
    console.error("Error fetching company watch item:", error);
    return null;
  }
};

export const fetchIndividualsForCompany = async (
  companyId: string
): Promise<PepWatchListData[] | null> => {
  try {
    const url = `${GET_KELDAN_API_URL()}/Pep/GetPepWatchList?companySearch=${encodeURIComponent(
      companyId
    )}&includeCount=true&start=0&limit=500`;
    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) return null;
    const data: IKeldanApiResponse<IPepWatchResponse<PepWatchListData[]>> = await response.json();
    if (!data.success || !data.result?.data) return null;
    return data.result.data;
  } catch (error) {
    console.error("Error fetching individuals for company:", error);
    return null;
  }
};

export const fetchCompanyWatchEvents = async (
  companyId: string,
  hoursSince: number = 24 * 365 * 10
): Promise<CompanyEventsData | null> => {
  try {
    const url = `${GET_KELDAN_API_URL()}/Pep/GetCompanyWatchEvents?companyId=${encodeURIComponent(
      companyId
    )}&hoursSince=${hoursSince}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) return null;
    const data: IKeldanApiResponse<IPepWatchResponse<CompanyEventsData>> = await response.json();
    if (!data.success || !data.result?.data) return null;
    return data.result.data;
  } catch (error) {
    console.error("Error fetching company watch events:", error);
    return null;
  }
};
